import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';

const DISMISSED_KEY = 'trial-expired-modal-dismissed';

export function TrialExpiredModal() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription, trialDaysRemaining } = useSubscription();
  const [open, setOpen] = useState(false);

  const isExpiredTrial =
    subscription?.status === 'trialing' && trialDaysRemaining === 0;

  useEffect(() => {
    if (isExpiredTrial && !sessionStorage.getItem(DISMISSED_KEY)) {
      setOpen(true);
    }
  }, [isExpiredTrial]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISSED_KEY, 'true');
  };

  if (!isExpiredTrial) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="items-center text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#D4AF37]/20 flex items-center justify-center mx-auto mb-2">
            <Crown className="h-8 w-8 text-[#B8860B]" />
          </div>
          <DialogTitle className="text-xl">
            {t('trial.expiredTitle')}
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {t('trial.expiredDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={() => {
              handleClose();
              navigate('/settings/subscription');
            }}
            className="w-full bg-[#B8860B] hover:bg-[#8B6914] text-white font-semibold"
          >
            {t('trial.expiredCta')}
          </Button>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="w-full text-neutral-500"
          >
            {t('trial.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
